import { useCallback, useEffect, useState } from 'react';
import { getApiSubscriptionUserIdProductParentId } from '../api/interconectar-api';
import { useAuth } from '../context/AuthContext';

interface SubscriptionUse {
	userSubscriptionId: string;
	userId: string;
	odooSubscriptionId: number | null;
	odooProductId: number | null;
	odooProductParentId: number | null;
	name: string;
	subscriptionCode: string;
	totalUses: number;
	usedCount: number;
	remainingUses: number;
	isInternal: boolean;
	productDetail: string | null;
	productCount?: number;
	countryCount?: number;
}

interface RemainingCalculationsResponse {
	results: SubscriptionUse[];
	availableSubscriptionsCount: number;
}

interface SubscriptionUsesResponse {
	results: SubscriptionUse[];
	hasMoreResults: boolean | null;
	continuationToken: string | null;
	status: string;
	successMessage: string;
	errors: string[];
	validationErrors: any[];
}

export const useRemainingPacks = (productId: string | null) => {
	const { state } = useAuth();
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState<string | null>(null);
	const [data, setData] = useState<RemainingCalculationsResponse | null>(null);

	const fetchData = useCallback(async () => {
		if (!state?.isAuthenticated) {
			setData(null);
			setLoading(false);
			return;
		}
		if (!productId) {
			setData(null);
			setLoading(false);
			return;
		}

		setLoading(true);
		setError(null);

		try {
			const userId = localStorage.getItem('userId');
			if (!userId) {
				throw new Error('User ID not found');
			}

			const response = (await getApiSubscriptionUserIdProductParentId(
				userId,
				parseInt(productId),
			)) as unknown as SubscriptionUsesResponse;

			if (response?.results && response.results.length > 0) {
				// Filtrar las suscripciones internas (isInternal: true) y por odooProductParentId
				const nonInternalResults = response.results.filter(
					(subscription) => !subscription.isInternal && subscription.odooProductParentId === parseInt(productId),
				);

				const filteredResults = nonInternalResults.map((subscription) => {
					// Parse productDetail to extract productCount and countryCount
					let productCount: number | undefined;
					let countryCount: number | undefined;

					if (subscription.productDetail) {
						const match = subscription.productDetail.match(/(\d+)\s+producto\w*\s+en\s+(\d+)\s+país\w*/);
						if (match) {
							productCount = parseInt(match[1], 10);
							countryCount = parseInt(match[2], 10);
						}
					}

					return {
						...subscription,
						productCount,
						countryCount,
					};
				});

				console.log('🚀 ~ fetchData ~ filteredResults:', filteredResults);

				const availableSubscriptions = filteredResults.filter((subscription) => (subscription.remainingUses ?? 0) > 0);
				// console.log('🚀 ~ fetchData ~ availableSubscriptions:', availableSubscriptions);
				setData({
					results: filteredResults,
					availableSubscriptionsCount: availableSubscriptions.length,
				});
			} else {
				throw new Error('No subscription data found');
			}
		} catch (err) {
			setError(err instanceof Error ? err.message : 'An error occurred');
			setData(null);
		} finally {
			setLoading(false);
		}
	}, [productId, state?.isAuthenticated]);

	useEffect(() => {
		if (state?.isAuthenticated) {
			fetchData();
		} else {
			setData(null);
			setLoading(false);
		}
	}, [fetchData, state?.isAuthenticated]);

	return {
		loading: state?.isAuthenticated ? loading : false,
		error,
		data,
		refetch: fetchData,
	};
};
